//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { selectActiveFileOrigin, selectCurrentPath, selectFileOriginType } from '@/redux/selectors/fileManagerSelectors';
import { Grid, IconButton } from '@mui/material';
import { clearForwardLocations, refreshFiles, setHomePath, setPath } from '@/redux/slices/fileManagerSlice';
import { isEqual } from 'lodash';
import Breadcrumbs from '../../../_common/Breadcrumbs';
import PathLink from './PathLink';
import GroupedPaths from './GroupedPaths';
import qumuloLogo from '@/assets/images/qumulo_logo.png';
import PolarisIcon from '@/components/_common/PolarisIcon';
import S3Icon from '../../../_common/Icons/S3Icon';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const Path = ({ withHome = false, homeBucket = false }) => {
  const currentPath = useAppSelector(selectCurrentPath) || [];
  const dispatch = useAppDispatch();
  const pathLength = currentPath.length - 1;
  const activeOriginType = useAppSelector(selectFileOriginType);
  const activeFileOriginSlug = useAppSelector(selectActiveFileOrigin).slug;
  const isAwsS3: boolean = activeOriginType === 'aws_s3';
  // currently the only way we know if it's qumulo
  const isQumulo: boolean = currentPath[0]?.toLowerCase()?.includes('qumulo');

  const handleClick = (i: number) => {
    const newPath = currentPath.slice(0, i + 1);
    if (!isEqual(currentPath, newPath)) {
      dispatch(setPath(newPath));
      dispatch(refreshFiles());
    }
  };

  const handleHomeClick = () => {
    if (homeBucket) {
      dispatch(setPath([currentPath[0]]));
    } else {
      dispatch(clearForwardLocations());
      dispatch(setHomePath());
    }
  };

  return (
    <Grid container alignItems="center" wrap="nowrap" sx={{ minHeight: 40 }}>
      <Breadcrumbs>
        {withHome && (
          <IconButton color="primary" onClick={handleHomeClick} size="small">
            <PolarisIcon icon="home" height={16} />
          </IconButton>
        )}
        {currentPath.length <= 3
          ? currentPath.map((stage: string, index: number) => {
              return (
                <Grid container alignItems="center" key={stage}>
                  {stage === activeFileOriginSlug && <FilePathIcon isAWS={isAwsS3} isQumulo={isQumulo} />}
                  <PathLink onClick={handleClick} key={stage + index} stage={stage} index={index} fullWidth={index === 0} />
                </Grid>
              );
            })
          : [
              <PathLink onClick={handleClick} key={currentPath[0]} stage={currentPath[0]} index={0} fullWidth />,
              <GroupedPaths onClick={handleClick} key="grouped-paths" />,
              <PathLink onClick={handleClick} key={currentPath[pathLength]} stage={currentPath[pathLength]} index={pathLength} />
            ]}
      </Breadcrumbs>
    </Grid>
  );
};

export default Path;

type FilePathIconProps = {
  isAWS: boolean;
  isQumulo: boolean;
};
const FilePathIcon = ({ isAWS, isQumulo }: FilePathIconProps) => {
  if (isAWS) {
    return (
      <>
        <S3Icon style={{ height: 30, width: 30, marginRight: 0 }} />
        <span style={{ marginRight: 5, marginBottom: -1 }}>S3:</span>
      </>
    );
  } else if (isQumulo) {
    return <img src={qumuloLogo} alt="Qumulo logo" height={25} width={25} style={{ marginRight: 10 }} />;
  } else {
    return <StyledIcon icon="data" height={25} width={25} />;
  }
};

const StyledIcon = styled(PolarisIcon)`
  padding-right: 5px;
`;
